// src/lib/tax-rules-div43-bands.ts
//
// Div 43 capital works rate-band data, keyed by construction COMMENCED date.
// Sibling to tax-rules-2025-26.ts (per-FY rules) per the L48 carve-out:
// legislative-date-banded rules live in tax-rules-{rule-name}-bands.ts files
// because their lookup key is the user-supplied event date (here, the
// commenced date), not the FY of claim. See L59.
//
// Match-priority is encoded by array order (first match wins). Future
// band additions must respect the documented priority in
// docs/audits/B130-wave-2b-design.md §2.1:
//
//   1. BtR-flagged (post-9-May-2023, requires isBtR)
//   2. Short-term traveller post-1992 (4% / 25y)
//   3. 1984-1987 any-building (4% / 25y)
//   4. 1979-1984 traveller-only (2.5% / 40y)
//   5. Pre-1979 nil (eligible: false)
//   6. Post-1987 standard residential / non-residential (2.5% / 40y, catch-all)
//
// Last reviewed: May 2026 (B130 Wave 2B Phase 1).
// Review trigger: legislative change (new BtR-class incentive, new band
// addition); not annual. See ARCHITECTURE §18.

export interface Div43Band {
  /** Stable identifier — used as the band's primary key in tests + logs. */
  id: string;
  /** Human-readable label rendered in UI / AI prompt. */
  bandLabel: string;
  /** ISO yyyy-mm-dd (inclusive). null = open-ended past. */
  commencedFrom: string | null;
  /** ISO yyyy-mm-dd (inclusive). null = open-ended future. */
  commencedTo: string | null;
  /** Property classes this band applies to. */
  appliesTo: ("residential" | "non-residential" | "short-term-traveller")[];
  /** Optional opt-in flag gate (currently only "isBtR"). */
  requiresFlag?: "isBtR";
  /** Annual rate as decimal (0, 0.025, 0.04). */
  rate: number;
  /** Effective life in years (0, 25, 40). */
  lifeYears: number;
  /** False only for nil-case bands (pre-1979, 1979-1984 non-traveller). */
  eligible: boolean;
  /** Plain-English explanation surfaced to user + AI. */
  reason: string;
  /** ATO RPG / ITAA / Treasury Laws Amendment references. */
  sourceUrls: string[];
}

const RPG_2025_P51 =
  "https://www.ato.gov.au/forms-and-instructions/rental-properties-2025/capital-works-deductions";
const ITAA_1997_DIV_43 =
  "https://www.ato.gov.au/law/view/document?DocID=PAC/19970038/115-25";
const TREASURY_BTR_AMENDMENT_2024 =
  "https://www.legislation.gov.au/C2024A00075";

export const DIV43_RATE_BANDS: Div43Band[] = [
  // 1. BtR-flagged (priority 1, must come first because the post-1987
  //    standard catch-all also matches dates after 2023-05-10).
  {
    id: "btr-post-2023",
    bandLabel: "BtR post-9-May-2023",
    commencedFrom: "2023-05-10",
    commencedTo: null,
    appliesTo: ["residential"],
    requiresFlag: "isBtR",
    rate: 0.04,
    lifeYears: 25,
    eligible: true,
    reason:
      "Eligible Build-to-Rent development, construction commenced after 9 May 2023.",
    sourceUrls: [TREASURY_BTR_AMENDMENT_2024, RPG_2025_P51],
  },
  // 2. Short-term traveller post-1992 (priority 2, before the 1984-1987
  //    band because 1992-02-27+ also has post-1987 standard as a fallback,
  //    but priority dictates traveller-specific 4% wins for traveller class).
  {
    id: "short-term-traveller-post-1992",
    bandLabel: "Post-26-Feb-1992 short-term traveller",
    commencedFrom: "1992-02-27",
    commencedTo: null,
    appliesTo: ["short-term-traveller"],
    rate: 0.04,
    lifeYears: 25,
    eligible: true,
    reason:
      "Short-term traveller accommodation, construction commenced after 26-Feb-1992.",
    sourceUrls: [RPG_2025_P51, ITAA_1997_DIV_43],
  },
  // 3. 1984-1987 any-building 4%.
  {
    id: "any-building-1984-1987",
    bandLabel: "1984-1987 (4%)",
    commencedFrom: "1984-08-22",
    commencedTo: "1987-09-15",
    appliesTo: ["residential", "non-residential", "short-term-traveller"],
    rate: 0.04,
    lifeYears: 25,
    eligible: true,
    reason: "Construction commenced 22-Aug-1984 to 15-Sep-1987.",
    sourceUrls: [RPG_2025_P51, ITAA_1997_DIV_43],
  },
  // 4. 1979-1984 traveller-only at 2.5%.
  {
    id: "short-term-traveller-1979-1984",
    bandLabel: "1979-1984 short-term traveller",
    commencedFrom: "1979-08-22",
    commencedTo: "1984-08-21",
    appliesTo: ["short-term-traveller"],
    rate: 0.025,
    lifeYears: 40,
    eligible: true,
    reason:
      "Short-term traveller accommodation, construction commenced 22-Aug-1979 to 21-Aug-1984.",
    sourceUrls: [RPG_2025_P51, ITAA_1997_DIV_43],
  },
  // 5. 1979-1984 non-traveller nil.
  {
    id: "nil-1979-1984-residential",
    bandLabel: "1979-1984 (residential nil)",
    commencedFrom: "1979-08-22",
    commencedTo: "1984-08-21",
    appliesTo: ["residential", "non-residential"],
    rate: 0,
    lifeYears: 0,
    eligible: false,
    reason:
      "Construction commenced 22-Aug-1979 to 21-Aug-1984 — only short-term traveller accommodation qualifies for Div 43 in this window.",
    sourceUrls: [RPG_2025_P51, ITAA_1997_DIV_43],
  },
  // 6. Pre-1979 nil.
  {
    id: "nil-pre-1979",
    bandLabel: "Pre-22-Aug-1979 (nil)",
    commencedFrom: null,
    commencedTo: "1979-08-21",
    appliesTo: ["residential", "non-residential", "short-term-traveller"],
    rate: 0,
    lifeYears: 0,
    eligible: false,
    reason:
      "Construction commenced before 22 August 1979 — no Div 43 capital works deduction available.",
    sourceUrls: [RPG_2025_P51, ITAA_1997_DIV_43],
  },
  // 7. Post-1987 standard residential / non-residential (catch-all eligible).
  {
    id: "post-1987-standard",
    bandLabel: "Post-15-Sep-1987 standard",
    commencedFrom: "1987-09-16",
    commencedTo: null,
    appliesTo: ["residential", "non-residential"],
    rate: 0.025,
    lifeYears: 40,
    eligible: true,
    reason: "Construction commenced after 15-Sep-1987.",
    sourceUrls: [RPG_2025_P51, ITAA_1997_DIV_43],
  },
];
